/**
 * THE DASHBOARD SHELL - one measure, one gutter, one vertical rhythm, shared
 * by `Page.tsx`, `ExposureTab.tsx` and `RecordTab.tsx`. Measured, both DOMs,
 * 1440x1200 / dpr 1.25:
 *
 *              board pt   board pb   board gap   board file
 *   buildings      12         24         10      Main.dc.html
 *   exposure       20          8         26      DashboardExposure.dc.html
 *   record         12         10         10      DashboardRecord.dc.html
 *   all three      10          0          9      Dashboard*Mobile.dc.html (375)
 *
 * Three rhythms, not one: Exposure's blocks are standing panels, the other two
 * a head over a table. Measure, gutter and mobile step are shared. The bottom
 * pad is only last-block-to-rule, because `DashboardFooter` is a full-bleed
 * sibling BELOW this container. Vertical carries two values; only 375 and 1440
 * are drawn.
 *
 * THE VERTICAL BOUNDARY IS 640, NOT `lg`: 1024 is where the RAIL docks, and
 * docking SHRINKS the measure (`Geometry.dc.html`, off the shell):
 *
 *      V      content    the card's 41.6px of side padding, as a share
 *     375       335       12.4%   ← the board strips the card here
 *     640       576        7.2%
 *     768       704        5.9%
 *    1023       959        4.3%   ← the widest the surface ever is
 *    1024       672        6.2%   ← the rail docks; content DROPS 287px
 *    1440      1088        3.8%   ← the board draws the card here
 *
 * 640 is this surface's only other boundary: `PORTAL_ROW_COMPACT_MEDIA_QUERY`
 * (639.98px), `hidden sm:grid`, `px-5 sm:px-8`.
 */
import { cn } from "@/lib/utils";

/** The content cap, frozen by ruling. Masthead, body and footer all sit on
 *  it, so the masthead `border-b` and footer `border-t` run the full main
 *  column rather than inset by the gutter. */
export const DASHBOARD_MEASURE =
  "mx-auto w-full max-w-[1180px] 2xl:max-w-[1320px]";

/** `Main.dc.html`: "PADDING IS NOT ONE NUMBER: px-5/sm:px-8/lg:px-11/2xl:px-14 = 20/32/44/56 per side." */
export const DASHBOARD_GUTTER = "px-5 sm:px-8 lg:px-11 2xl:px-14";

/**
 * THE SQUARE CHIP - the boards' bordered mono tag, box only. `.chip-neutral`,
 * `.chip-amber-sq` and `.chip-solid` in `DashboardExposure.dc.html` are one
 * box under three colour names: `border-radius: 4px; padding: 3px 7px`, 13px
 * mono, `.06em`. Only border and ink change, so only those belong at the call
 * site: the box computes 3 + 17.6 + 3 + 0.8 + 0.8 = 25.2 high, where a
 * hand-rolled `px-[6px] py-[1px]` computes 21.2.
 *
 * The PILLS are different boxes, not this at another radius:
 * `.chip-neutral-pill` 2px 7px radius 999, `.chip-teal` 4px 10px.
 */
export const DASHBOARD_CHIP_SQUARE =
  "rounded-[4px] border px-[7px] py-[3px] font-mono text-[13px] uppercase tracking-[0.06em]";

/**
 * THE AMBER CHIP IS THREE DECLARATIONS, and the ground is the one re-derived
 * from memory. `Main.dc.html:144` and `DashboardRecord.dc.html:158` agree:
 *
 *   color:      var(--dc-risk-med)        #fcd34d dark · #b45309 light
 *   border:     var(--dc-risk-med-line)   #685227 dark · #fcd34d light
 *   background: var(--dc-risk-med-soft)   #433519 dark · #fffbeb light
 *
 * Every amber chip on every board carries that ground in both themes.
 * `.chip-neutral` really does declare only colour and border, so a transparent
 * ground THERE is the board's own and gets no token.
 */
export const DASHBOARD_CHIP_AMBER =
  "border-dc-risk-med-line bg-dc-risk-med-soft text-dc-risk-med";

/**
 * THE STANDING PANEL - the `.panel` class's box, not every bordered card.
 * `DashboardRecord.dc.html:224`, repeated in `DashboardExposure.dc.html`:
 *
 *   .panel { border: 1px solid var(--dc-line); background: var(--dc-panel);
 *            border-radius: 10px; padding: 12px 16px; }
 *
 * and the ruling three lines above it:
 *
 *   "Hierarchy WITHOUT cards. Three devices, in order of preference:
 *      1. a 1px --ink-line rule between rows        (the default)
 *      2. a --paper-panel fill on the row that matters
 *      3. a bordered 10px panel                     (only for a standing block)
 *    A 12px radius appears ONLY on chat surfaces. Never nest a panel in a panel."
 *
 * 10 IS THE CARD RADIUS; every other rung of `Main.dc.html:112-120` is spent
 * on a non-panel, so a 6 or an 8 on a card is the wrong object:
 *
 *   999px  a STATE OF A THING   (posture node, engagement pill, hedge banner)
 *     10px a STANDING PANEL     (this)
 *      6px a TEXT INPUT         (the SEARCH box; the rail's nav cells)
 *      5px a BUTTON or an ARROW (`.btn-ghost`, the pager's two arrows)
 *      4px a STAMP ON A ROW     (`DASHBOARD_CHIP_SQUARE`)
 *      3px the densest row stamp
 *
 * THE 6/5 SPLIT IS INPUT-vs-BUTTON, NOT ACTION-vs-SEGMENT. Every
 * `Main.dc.html` node with a 3-12px radius, by computed value:
 *
 *   SEARCH ADDRESS OR BIN   radius 6   pad 5px 10px   h 29.2   16px Inter
 *   `.t-cell` rail items    radius 6   pad 5px  8px   h 28.4   15px Inter
 *   EXPORT CSV `.btn-ghost` radius 5   pad 5px 10px   h 29.2   13px mono 0.08em
 *   the pager's two arrows  radius 5   pad 0          h 23.6
 *   50 PER PAGE `.chip-*`   radius 4   pad 3px  7px   h 25.2   ← the chip token
 *   every row stamp         radius 3   pad 3px 7-8px  h 19-20.6
 *
 * `DashboardRecord.dc.html`'s EXPORT CSV measures the same 5 at 125x31.6.
 *
 * `border` carries no colour: `--dc-line` is the default, and the two callers
 * with a tone (`--dc-risk-med-line` on a hedge, the teal pair on a clean
 * record) state their own pair beside this.
 */
export const DASHBOARD_PANEL = "rounded-[10px] border px-4 py-3";

/**
 * THE PROSE NOTE - the one Inter tier these boards draw for a sentence.
 * Re-censused 2026-09-17, both DOMs, every text-bearing node, by resolved
 * `font-family`. `DashboardRecord.dc.html` serves four Inter tiers:
 *
 *   14px/19.6px   x9    `.t-note`   - the standing sentence. THIS TOKEN.
 *   14px/21.7px   x18   `.tt-body`  - tooltip interiors only (1.55)
 *   16px/24px     x3    `.t-body`   - the tab strip's three labels
 *   15px/normal   x4    `.t-cell`   - table-cell prose (18.15 at 15px)
 *
 * BUCKET BY RESOLVED FAMILY, NOT CLASS: the 16px/17px x14 nodes are
 * `.t-building`-family NEWSREADER, not Inter.
 *
 * NO 13px INTER EXISTS ON EITHER BOARD - small secondary text is MONO at 13
 * (`.s-meta`, `.s-inline`, `.s-stamp`) and a sentence steps UP to Inter 14.
 * NOT FOR a label (mono; see `DASHBOARD_LEADING`), a tooltip interior
 * (`.tt-body` 1.55), or the tab strip (`.t-body` 16/1.5).
 */
export const DASHBOARD_NOTE_TIER = "text-[14px] leading-[1.4]";

/**
 * THE MEASURE A STANDING SENTENCE IS SET TO - it always travels with
 * `DASHBOARD_NOTE_TIER`, and four values (760 / 660 / 640 / 62ch) were once in
 * flight across six files, one of them in a unit the others cannot be compared
 * to.
 *
 * IT IS A CONVENTION, NOT A BOARD NUMBER: `DashboardExposure.dc.html` draws no
 * standing Inter paragraph at all (every explanatory run is mono `.s-inline`
 * at 13, capped by its COLUMN - 575.8 in the 590 track, 472 in the 472 track)
 * and `DashboardRecord.dc.html`'s `.t-note` nodes are table-cell text at
 * 198.4. 760 is the plurality value, named so the next sentence is not a
 * fifth.
 */
export const DASHBOARD_PROSE_MEASURE = "max-w-[760px]";

/**
 * THE GAP BETWEEN TWO EXPOSURE SECTIONS: `DashboardExposure.dc.html` draws
 * `gap: 26px`, `DashboardExposureMobile.dc.html` 9 between the same four
 * sections (children at y378.4 / 576.7 / 884.5 / 1138.5).
 *
 * EXPORTED because Exposure nests its sections two levels down - a `grid`,
 * then one flex column per track - and at `lg` both gaps ARE this distance, so
 * a flat `gap-[26px]` at any of them puts 26 between sections at 375. The step
 * is `sm:` (640), not `lg:` (1024, where the RAIL docks).
 */
export const DASHBOARD_EXPOSURE_SECTION_GAP = "gap-[9px] sm:gap-[26px]";

/** THE GAP INSIDE ONE SECTION, head row to body: desktop 7, mobile 5.
 *  `DashboardExposure.dc.html` measures head→body 7 on BY CHARGE CODE
 *  (616.5 → 658.7, head 35.2 + 7); the mobile board draws 5 on three of four
 *  sections and 6 on the fourth, so the value three agree on wins. */
export const DASHBOARD_SECTION_INNER_GAP = "gap-[5px] sm:gap-[7px]";

/**
 * THE GAP BETWEEN TWO STACKED BLOCKS - Buildings and Record: 10 at 1440
 * (`Main.dc.html`, `DashboardRecord.dc.html`), 9 at 375
 * (`DashboardMobile.dc.html`, `DashboardRecordMobile.dc.html`). Exposure keeps
 * its own larger pair; its blocks are standing panels.
 *
 * EXPORTED because `RecordTab`'s handled group is a `<section>` holding three
 * of these blocks - the same relationship, and Record's inner containers only
 * ever run 3/4/5/7/10/12/14/20/26. A WRONG VALUE THERE IS INVISIBLE: two of
 * that section's three children self-gate to null on this tenant, and a gap
 * between one thing lays out as nothing until a second block has data.
 */
export const DASHBOARD_BLOCK_GAP = "gap-[9px] sm:gap-[10px]";

/** Per-tab vertical rhythm. `gap-*`, not `space-y-*`: the body is a flex
 *  column and `space-y` is a descendant selector at (0,3,0) that silently
 *  beats any `mt-*` a child sets - which is how `Page.tsx`'s wordmark carried
 *  an `mt-[28px]` that never rendered once. */
/**
 * ALL THREE TABS OPEN THEIR CONTENT ON ONE LINE (Leo, 2026-09-22). Measured in
 * the browser at 1600, Related, masthead bottom y232.5: exposure's first card
 * 252.5, buildings' H2 252.3, record 252.5. `sm:pt-5` on exposure and record;
 * buildings reaches it as pt-3 + the section's own `sm:mt-[2px]`, because its
 * H2 sits centred in a taller row than it is.
 */
const DASHBOARD_RHYTHM = {
  buildings: `pt-[10px] pb-0 sm:pt-3 sm:pb-6 ${DASHBOARD_BLOCK_GAP}`,
  exposure: `pt-[10px] pb-0 sm:pt-5 sm:pb-2 ${DASHBOARD_EXPOSURE_SECTION_GAP}`,
  record: `pt-[10px] pb-0 sm:pt-5 sm:pb-[10px] ${DASHBOARD_BLOCK_GAP}`,
} as const;

export type DashboardTabName = keyof typeof DASHBOARD_RHYTHM;

/**
 * The body container for one tab.
 *
 * `relative` IS PART OF THE CONTRACT: Tailwind's `sr-only` is
 * `position: absolute`, so a spoken twin resolves against its nearest
 * POSITIONED ancestor - with none, the initial containing block, where it can
 * push the document wider than the viewport.
 *
 * `[&>*]:min-w-0` IS LOAD-BEARING: a flex item's `min-width` defaults to
 * `auto` (intrinsic content width), so a section holding an `overflow-x-auto`
 * table refuses to shrink and pushes the BODY into horizontal scroll at 1024.
 */
/**
 * THE INHERITED LINE-HEIGHT, RESET TO THE BOARDS' ONE.
 *
 * Tailwind's preflight sets `html { line-height: 1.5 }` - 19.5px on the 13px
 * mono ramp. The boards declare NO line-height on `.mono` or its five label
 * tiers (`.s-eyebrow` 0.12em, `.s-colhead` 0.1em, `.s-stamp` 0.08em,
 * `.s-meta` 0.07em, `.s-inline` 0.06em - `DashboardRecord.dc.html:89-92`), so
 * all five resolve to `normal`: 17.6px at 13px in JetBrains Mono, ~1.9px
 * tighter per line than the inherited 1.5.
 *
 * PROSE THAT STATES ITS OWN LEADING IS UNAFFECTED, AND PROSE THAT DOES NOT IS
 * NOT: anything here omitting one renders `normal`, not preflight's 1.5, so a
 * new block must state one. The Inter tiers carry explicit `leading-[1.4] /
 * [1.45] / [1.5] / [1.55]`.
 *
 * 16 IS A REAL NUMBER IN THE WRONG PLACE. Family-resolved census of
 * `Main.dc.html`, every mono leaf by `size | line-height | letter-spacing`:
 *
 *   13 | normal | 0.78px   x26   `.s-inline`  - the caveat tier. THIS TOKEN.
 *   13 | 13px   | 0.78px   x5    chips only (`.chip-amber-sq`, `.chip-neutral`)
 *   13 | 16px   | 1.3px    x3    the group-band labels   (0.1em)
 *   13 | 16px   | 1.495px  x1    the DATED section head  (0.115em)
 *   14 | 16px   | normal   x6    the band subtotal cells
 *   13 | 16px   | 0.78px   x0    ← does not exist on any board
 *
 * So 16 is the BAND/HEAD tier at 0.1em and 0.115em and may not travel down to
 * 0.06em. State it once here, never `leading-[normal]` per call site: both
 * component involved (`PortalBuildingsTable`) mounts ONLY
 * from the Dashboard, and Chrome's UA sheet pins the `input` and `select`
 * among them to `normal` regardless.
 */
const DASHBOARD_LEADING = "leading-[normal]";

export const dashboardBodyClass = (tab: DashboardTabName, extra?: string) =>
  cn(
    "relative flex flex-col [&>*]:min-w-0",
    DASHBOARD_LEADING,
    DASHBOARD_MEASURE,
    DASHBOARD_GUTTER,
    DASHBOARD_RHYTHM[tab],
    extra,
  );
